import React, { useRef, useState } from 'react';
import { deleteLscLeagueLogo, uploadLscLeagueLogo, LscApiError } from '../api';
import type { LscLeague } from '../types';
import ErrorBanner from './ErrorBanner';
import LeagueLogo from './LeagueLogo';
import { LEAGUE_LOGO_ACCEPT, LEAGUE_LOGO_COPY, validateLeagueLogoFile } from '../leagueBranding';

interface LeagueLogoPanelProps {
  league: LscLeague;
  leagueArchived?: boolean;
  onUpdated?: (league: LscLeague) => void;
}

const LeagueLogoPanel: React.FC<LeagueLogoPanelProps> = ({
  league,
  leagueArchived = false,
  onUpdated,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasLogo = Boolean(league.logo_url);
  const busy = uploading || removing;

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || leagueArchived) return;

    const validationError = validateLeagueLogoFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const updated = await uploadLscLeagueLogo(league.id, formData);
      onUpdated?.(updated);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Logo upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!hasLogo || leagueArchived) return;
    setRemoving(true);
    setError(null);
    try {
      const updated = await deleteLscLeagueLogo(league.id);
      onUpdated?.(updated);
    } catch (err) {
      setError(err instanceof LscApiError ? err.message : 'Could not remove logo.');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="card space-y-4" data-testid="league-logo-panel">
      <div>
        <h2 className="text-lg font-semibold text-white mb-1">{LEAGUE_LOGO_COPY.title}</h2>
        <p className="text-sm text-secondary-400">{LEAGUE_LOGO_COPY.helper}</p>
        <p className="text-xs text-secondary-500 mt-1">{LEAGUE_LOGO_COPY.recommended}</p>
        <p className="text-xs text-secondary-500">{LEAGUE_LOGO_COPY.acceptedTypes}</p>
      </div>

      <div className="flex items-center gap-4">
        <LeagueLogo league={league} size="preview" testId="league-logo-settings-preview" />
        <div className="text-sm text-secondary-400">
          {hasLogo ? 'Current league logo' : 'No logo uploaded yet'}
        </div>
      </div>

      {leagueArchived && (
        <p className="text-sm text-secondary-500">
          Logo cannot be changed while the league is archived.
        </p>
      )}

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}

      {!leagueArchived && (
        <div className="flex flex-wrap gap-3">
          <input
            ref={inputRef}
            type="file"
            accept={LEAGUE_LOGO_ACCEPT}
            className="sr-only"
            id={`league-logo-upload-${league.id}`}
            aria-label="Upload league logo"
            onChange={handleFileChange}
            disabled={busy}
            data-testid="league-logo-file-input"
          />
          <button
            type="button"
            className="btn-primary"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            data-testid="league-logo-upload-button"
          >
            {uploading ? 'Uploading...' : hasLogo ? 'Replace logo' : 'Upload logo'}
          </button>
          {hasLogo && (
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={handleRemove}
              data-testid="league-logo-remove-button"
            >
              {removing ? 'Removing...' : 'Remove logo'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LeagueLogoPanel;
